import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface QrScannerDialogProps {
  open: boolean;
  onClose: () => void;
}

interface BarcodeDetectorLike {
  detect: (source: HTMLVideoElement) => Promise<Array<{ rawValue?: string }>>;
}

function tokenFromScan(value: string): string {
  try {
    const url = new URL(value);
    const parts = url.pathname.split('/');
    const qIndex = parts.findIndex((part) => part === 'q');
    if (qIndex >= 0) return decodeURIComponent(parts[qIndex + 1] ?? '');
  } catch {
    // A Store QR reader may return only the opaque token.
  }
  return value.trim();
}

/**
 * Uses the native QR detector when available and ZXing otherwise. Both paths request the browser
 * camera; no QR token or camera data is persisted.
 */
export function QrScannerDialog({ open, onClose }: QrScannerDialogProps) {
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fallbackControlsRef = useRef<{ stop: () => void } | null>(null);
  const [manual, setManual] = useState('');
  const [status, setStatus] = useState('');
  const [cameraReady, setCameraReady] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement as HTMLElement | null;
    const focusable = () =>
      Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled])'
        ) ?? []
      );
    focusable()[0]?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const elements = focusable();
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previous?.focus();
    };
  }, [onClose, open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    let raf = 0;

    const completeScan = (value: string) => {
      const token = tokenFromScan(value);
      if (!token || cancelled) return;
      onClose();
      navigate(`/q/${encodeURIComponent(token)}`);
    };

    const startNativeScanner = async (
      detectorCtor: new (options: { formats: string[] }) => BarcodeDetectorLike
    ) => {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      if (cancelled) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
      setStatus('Đưa mã QR vào khung hình để bắt đầu.');
      const detector = new detectorCtor({ formats: ['qr_code'] });
      const tick = async () => {
        if (!videoRef.current || cancelled) return;
        try {
          const [result] = await detector.detect(videoRef.current);
          if (result?.rawValue) {
            completeScan(result.rawValue);
            return;
          }
        } catch {
          // Single-frame failures are normal while the camera is focusing.
        }
        raf = window.requestAnimationFrame(tick);
      };
      raf = window.requestAnimationFrame(tick);
    };

    const startZxingScanner = async () => {
      const { BrowserQRCodeReader } = await import('@zxing/browser');
      if (cancelled || !videoRef.current) return;
      const reader = new BrowserQRCodeReader();
      const controls = await reader.decodeFromConstraints(
        { video: { facingMode: { ideal: 'environment' } }, audio: false },
        videoRef.current,
        (result) => {
          if (result?.getText()) completeScan(result.getText());
        }
      );
      if (cancelled) {
        controls.stop();
        return;
      }
      fallbackControlsRef.current = controls;
      setCameraReady(true);
      setStatus('Đưa mã QR vào khung hình để bắt đầu.');
    };

    const start = async () => {
      const detectorCtor = (
        window as typeof window & {
          BarcodeDetector?: new (options: { formats: string[] }) => BarcodeDetectorLike;
        }
      ).BarcodeDetector;
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus('Thiết bị hoặc kết nối này không hỗ trợ camera. Bạn có thể dán mã QR bên dưới.');
        return;
      }
      try {
        if (detectorCtor) await startNativeScanner(detectorCtor);
        else await startZxingScanner();
      } catch {
        setStatus('Không thể mở camera. Vui lòng cấp quyền camera hoặc dán mã QR.');
      }
    };

    void start();
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
      fallbackControlsRef.current?.stop();
      fallbackControlsRef.current = null;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setCameraReady(false);
    };
  }, [navigate, onClose, open]);

  if (!open) return null;

  const submitManual = (event: React.FormEvent) => {
    event.preventDefault();
    const token = tokenFromScan(manual);
    if (!token) return;
    onClose();
    navigate(`/q/${encodeURIComponent(token)}`);
  };

  return (
    <div className="qr-scanner-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        ref={dialogRef}
        className="qr-scanner-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="qr-scanner-heading"
        aria-describedby="qr-scanner-status"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className="dialog-close" type="button" aria-label="Đóng quét QR" onClick={onClose}>
          ×
        </button>
        <p className="storefront-section-kicker">QR Order</p>
        <h2 id="qr-scanner-heading">Quét QR tại cửa hàng</h2>
        <div className={`qr-camera-frame${cameraReady ? ' is-ready' : ''}`}>
          <video ref={videoRef} muted playsInline aria-label="Camera quét mã QR" />
        </div>
        {status && (
          <p id="qr-scanner-status" className="qr-scanner-status" role="status">
            {status}
          </p>
        )}
        <form onSubmit={submitManual} className="qr-manual-form">
          <label>
            Dán mã QR hoặc đường dẫn QR
            <input
              value={manual}
              autoComplete="off"
              inputMode="url"
              onChange={(event) => setManual(event.target.value)}
              placeholder="Ví dụ: https://.../q/..."
            />
          </label>
          <button type="submit">Mở phiên gọi món</button>
        </form>
      </section>
    </div>
  );
}
